import React, { useState, useEffect, useContext, useRef } from "react";
import { StyleSheet, ScrollView, Text, TouchableOpacity, Modal as RNModal, Pressable } from "react-native";
import { View } from "../../../components/shared/Themed";
import { LabelBannerWithSearchBar } from "../../../components/custom/Banners/LabelBannerWithSearchBar";
import { LabelBannerWithSearchTags } from "../../../components/custom/Banners/LabelBannerWithSearchTags";
import { DataTableWithPagination } from "../../../components/custom/DataTable/DataTableWithPagination";
import Spinner from "../../../components/shared/Spinner";
import { getPmxDetailsInventory } from "../../../services/inventoryService";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import {
  headersPmxReport,
  mdHeadersPmxReport,
  smHeadersPmxReport,
} from "../utils/pmxInventoryHeaders";
import { validSignedURL } from "../../../utils/validateSignedUrl";
import { pmxDetailModel } from "../../../models/reportsModel";
import { useSelector } from "react-redux";
import { handleTableLoader } from "../../../utils/common";
import { userClaimInformation } from "../../../models/usersModel";
import { sortColumns } from "../../../utils/sortColumns";
import { filterBasedOnAnyKey } from "../../../utils/searchInArrays";
import { useIsFocused } from "@react-navigation/native";
import AsyncStorage from "@react-native-async-storage/async-storage";
// @ts-ignore
import { Feather } from "@expo/vector-icons";

export default function PmxDetailsInventoryReportScreen({
  route,
  navigation,
}: any) {
  const isFocused = useIsFocused();
  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);
  const {
    pmxInventoryDetailReport,
    pmxInventoryDetailFilterCategoryList,
    isPmxInventoryDetailReportListLoaded,
  } = useSelector((state: any) => state.reports);
  const { userInfo } = useSelector((state: any) => state.configurations);

  const [selected, setSelected] = useState<pmxDetailModel>(
    {} as unknown as pmxDetailModel
  );

  const [pmxDetailPageLevelList, setPmxDetailPageLevelList] = useState<
    Array<pmxDetailModel>
  >([]);
  const [isSearched, setIsSearched] = useState<boolean>(false);

  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      headersPmxReport
    );
  const [searchedText, setSearchedText] = useState<string>("");
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [isImageLoading, setImageLoading] = useState<boolean>(true);

  // Column visibility settings
  const [columnSettingsOpen, setColumnSettingsOpen] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [settingsBtnPos, setSettingsBtnPos] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const settingsBtnRef = useRef<any>(null);

  // Load saved hidden columns from storage on mount
  useEffect(() => {
    AsyncStorage.getItem("pmx_details_inventory_report_hidden_columns").then((stored) => {
      if (stored) {
        try {
          setHiddenColumns(JSON.parse(stored));
        } catch { }
      }
    });
  }, []);

  // All available headers based on device size
  const getAllHeaders = () => {
    return isLargeDevice ? headersPmxReport : isMediumDevice ? mdHeadersPmxReport : smHeadersPmxReport;
  };

  const getFilteredHeaders = (baseHeaders: Record<string, Record<string, string | number | boolean>>) => {
    const visibleEntries = Object.entries(baseHeaders).filter(([key]) => !hiddenColumns.includes(key));
    const totalOriginalFlex = Object.values(baseHeaders).reduce((sum, h) => sum + (Number(h.width) || 0), 0);
    const totalVisibleFlex = visibleEntries.reduce((sum, [, h]) => sum + (Number(h.width) || 0), 0);
    const scale = totalVisibleFlex > 0 ? totalOriginalFlex / totalVisibleFlex : 1;

    const result: Record<string, Record<string, string | number | boolean>> = {};
    visibleEntries.forEach(([key, value]) => {
      result[key] = { ...value, width: Number(value.width) * scale };
    });
    return result;
  };

  // Update table headers when hidden columns change or device size changes
  useEffect(() => {
    const allHeaders = getAllHeaders();
    setTableHeaders(getFilteredHeaders(allHeaders));
  }, [hiddenColumns, isLargeDevice, isMobileDevice, isMediumDevice]);

  const toggleColumn = (columnKey: string) => {
    setHiddenColumns((prev) => {
      const updated = prev.includes(columnKey)
        ? prev.filter((k) => k !== columnKey)
        : [...prev, columnKey];
      AsyncStorage.setItem("pmx_details_inventory_report_hidden_columns", JSON.stringify(updated));
      return updated;
    });
  };

  const columnsToNotHaveThousandsSeparator: Array<string> = [
    "Batch number",
    "SSCC number",
    "Uom1",
    "Uom2",
  ];

  const [_filterCategory, _setFilterCategory] = useState<string>("all");

  const displayTableLoader = () => {
    handleTableLoader(true, false, setIsSearched);
  };

  const fadeTableLoader = () => {
    handleTableLoader(false, true, setIsSearched);
  };

  const onHandleSortColumn = (columnKey: string) => {
    const _sortedPmxDetailList: Array<pmxDetailModel> = sortColumns(
      pmxDetailPageLevelList,
      columnKey,
      tableHeaders,
      setTableHeaders
    );

    setPmxDetailPageLevelList(_sortedPmxDetailList);
  };

  const handleSearch = (searchText: string) => {
    displayTableLoader();

    if (searchText.trim() !== "") {
      setSearchedText(searchText);

      const filteredListBasedOnSearchOrCondition: Array<pmxDetailModel> =
        filterBasedOnAnyKey(pmxInventoryDetailReport, searchText);

      const _filterPmxByCategory = filterPmxByCategory(
        filteredListBasedOnSearchOrCondition,
        _filterCategory
      );
      setPmxDetailPageLevelList(recordTransformation(_filterPmxByCategory));
    } else {
      setSearchedText("");
      const _filterPmxByCategory = filterPmxByCategory(
        pmxInventoryDetailReport,
        _filterCategory
      );
      setPmxDetailPageLevelList(recordTransformation(_filterPmxByCategory));
    }

    setTimeout(() => {
      fadeTableLoader();
    }, 1000);
  };

  const recordTransformation = (data: Array<pmxDetailModel>) => {
    const pmxData: Array<pmxDetailModel> = [];
    data.forEach((item: pmxDetailModel) => {
      pmxData?.push({
        ...item,
        BatchNumber1: item.BatchNumber1 === null ? "N/A" : item.BatchNumber1,
        SsccNumber: item.SsccNumber === null ? "N/A" : item.SsccNumber,
        QuantityUom:
          item.QuantityUom === null
            ? "0"
            : `${(item.QuantityUom as unknown as number)?.toLocaleString()}`,
        QuantityUom2:
          item.QuantityUom2 === null
            ? "0"
            : `${(item.QuantityUom2 as unknown as number)?.toLocaleString()}`,
      });
    });

    return pmxData;
  };

  const filterPmxByCategory = (
    _pmxsReport: Array<pmxDetailModel>,
    _filterCategory: string
  ) => {
    return _pmxsReport.filter((pmx: pmxDetailModel) => {
      switch (_filterCategory) {
        case "NSW":
          return pmx.Warehouse === "01";
          break;
        case "VIC":
          return pmx.Warehouse === "04";
          break;
        case "QLD":
          return ["02", "14"].includes(pmx.Warehouse);
          break;
        case "TAS":
          return ["06", "16", "12"].includes(pmx.Warehouse);
          break;
        case "WA":
          return pmx.Warehouse === "05";
          break;
        default:
          return pmx;
          break;
      }
    });
  };

  useEffect(() => {
    let isMounted: boolean = true;
    const _userInfo = userInfo as userClaimInformation;

    // Avoid keep refreshing table when item is selected
    if (isMounted && modalVisible === false) {
      if (pmxDetailPageLevelList || isRefresh) {
        displayTableLoader();

        _setFilterCategory("all");
        setSearchedText("");

        getPmxDetailsInventory(
          "",
          _userInfo.team,
          _userInfo.zone_info || "",
          tableHeaders
        );
      }
    }

    if (Object.keys(selected).length !== 0) {
      validSignedURL(
        selected.ItemCode,
        "label",
        setImageLoading,
        setSelectedImage
      );
    }

    const allHeaders = getAllHeaders();
    setTableHeaders(getFilteredHeaders(allHeaders));

    setRefresh(false);

    return () => {
      isMounted = false;
    };
  }, [isFocused, selected, isRefresh]);

  useEffect(() => {
    setImageLoading(true);
  }, [modalVisible]);

  useEffect(() => {
    setPmxDetailPageLevelList(recordTransformation(pmxInventoryDetailReport));

    setTimeout(() => {
      fadeTableLoader();
    }, 2000);
  }, [pmxInventoryDetailReport]);

  useEffect(() => {
    displayTableLoader();

    const _pmxsReport = recordTransformation(
      pmxInventoryDetailReport
    ) as Array<pmxDetailModel>;
    const filteredListBasedOnSearchOrCondition =
      searchedText.trim().length > 0
        ? filterBasedOnAnyKey(_pmxsReport, searchedText)
        : _pmxsReport;

    setPmxDetailPageLevelList(
      filterPmxByCategory(filteredListBasedOnSearchOrCondition, _filterCategory)
    );

    fadeTableLoader();
  }, [_filterCategory]);

  const navigateToOrder = (order: any, action: string) => {
    navigation.navigate("PmxDetailsInventoryDetailReportScreen", { order });
  };

  return (
    <ScrollView style={{ backgroundColor: "white" }}>
      <View style={[styles.container, { backgroundColor: "#f2f2f2" }]}>
        <View
          style={[
            styles.containerContent,
            {
              width: "100%",
            },
          ]}
        >
          {isPmxInventoryDetailReportListLoaded ? (
            <View>
              <LabelBannerWithSearchBar
                titleName={`Total Items: ${pmxDetailPageLevelList.length}`}
                handleSearch={handleSearch}
                setRefresh={setRefresh}
                dataToExport={pmxDetailPageLevelList}
                fileName={"inventory_details_report"}
                extraControls={(
                  <TouchableOpacity
                    ref={settingsBtnRef}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      backgroundColor: "#F3F4F6",
                      justifyContent: "center",
                      alignItems: "center",
                      borderWidth: 1,
                      borderColor: columnSettingsOpen ? "#818CF8" : "#E2E8F0",
                    }}
                    onPress={() => {
                      if (settingsBtnRef.current) {
                        settingsBtnRef.current.measureInWindow(
                          (x: number, y: number, width: number, height: number) => {
                            setSettingsBtnPos({ x: x || 0, y: y || 0, width: width || 0, height: height || 0 });
                            setColumnSettingsOpen(!columnSettingsOpen);
                          }
                        );
                      } else {
                        setColumnSettingsOpen(!columnSettingsOpen);
                      }
                    }}
                  >
                    <Feather
                      name="settings"
                      size={15}
                      color={columnSettingsOpen ? "#56B7FD" : "#6B7280"}
                    />
                  </TouchableOpacity>
                )}
              />
              {userInfo.team === "operation" && (
                <LabelBannerWithSearchTags
                  isMobileDevice={isMobileDevice}
                  isSmallDevice={isSmallDevice}
                  label="Quick Filter"
                  filterCategoryList={pmxInventoryDetailFilterCategoryList}
                  filterCategory={_filterCategory}
                  setFilterCategory={_setFilterCategory}
                />
              )}
              <DataTableWithPagination
                headers={tableHeaders}
                rows={pmxDetailPageLevelList}
                itemsPerPage={100}
                useModal={true}
                isRowsExpandable={false}
                setModalVisible={setModalVisible}
                setSelected={setSelected}
                isDetail={true}
                isDataLoading={isSearched}
                columnsToNotHaveThousandSeparator={
                  columnsToNotHaveThousandsSeparator
                }
                handleRowClick={() => { }}
                handleDeleteRow={() => { }}
                handleSortColumn={onHandleSortColumn}
                operationActions={navigateToOrder}
              />
              {columnSettingsOpen && (
                <RNModal
                  transparent={true}
                  visible={columnSettingsOpen}
                  animationType="fade"
                  onRequestClose={() => setColumnSettingsOpen(false)}
                >
                  <Pressable
                    style={{
                      flex: 1,
                      backgroundColor: "rgba(0,0,0,0.1)",
                    }}
                    onPress={() => setColumnSettingsOpen(false)}
                  >
                    <Pressable
                      style={{
                        position: "absolute",
                        top: settingsBtnPos.y + settingsBtnPos.height + 6,
                        left: settingsBtnPos.x - 180 + 40,
                        backgroundColor: "#fff",
                        borderRadius: 12,
                        borderWidth: 1,
                        borderColor: "#E2E8F0",
                        paddingVertical: 10,
                        minWidth: 220,
                        shadowColor: "#000",
                        shadowOffset: { width: 0, height: 8 },
                        shadowOpacity: 0.15,
                        shadowRadius: 20,
                        elevation: 10,
                      }}
                      onPress={(e) => e.stopPropagation()}
                    >
                      <Text
                        style={{
                          fontSize: 12,
                          fontWeight: "700",
                          color: "#6B7280",
                          paddingHorizontal: 16,
                          paddingBottom: 8,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Show / Hide Columns
                      </Text>
                      <View style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 4 }} />
                      {/* Select All */}
                      <TouchableOpacity
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          paddingVertical: 8,
                          paddingHorizontal: 16,
                        }}
                        onPress={() => {
                          const allKeys = Object.keys(getAllHeaders()).filter((k) => k !== "");
                          if (hiddenColumns.length === 0) {
                            const updated = [...allKeys];
                            setHiddenColumns(updated);
                            AsyncStorage.setItem("pmx_details_inventory_report_hidden_columns", JSON.stringify(updated));
                          } else {
                            setHiddenColumns([]);
                            AsyncStorage.setItem("pmx_details_inventory_report_hidden_columns", JSON.stringify([]));
                          }
                        }}
                      >
                        <View
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: 4,
                            borderWidth: 1.5,
                            borderColor: hiddenColumns.length === 0 ? "#56B7FD" : "#D1D5DB",
                            backgroundColor: hiddenColumns.length === 0 ? "#56B7FD" : "#fff",
                            justifyContent: "center",
                            alignItems: "center",
                            marginRight: 10,
                          }}
                        >
                          {hiddenColumns.length === 0 && (
                            <Feather name="check" size={13} color="#fff" />
                          )}
                        </View>
                        <Text
                          style={{
                            fontSize: 14,
                            color: "#111827",
                            fontWeight: "700",
                          }}
                        >
                          Select All
                        </Text>
                      </TouchableOpacity>
                      <View style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 2 }} />
                      <ScrollView style={{ maxHeight: 300 }} showsVerticalScrollIndicator={true}>
                        {Object.entries(getAllHeaders()).map(([key, value]) => {
                          if (key === "") return null;
                          const isVisible = !hiddenColumns.includes(key);
                          return (
                            <TouchableOpacity
                              key={key}
                              style={{
                                flexDirection: "row",
                                alignItems: "center",
                                paddingVertical: 8,
                                paddingHorizontal: 16,
                              }}
                              onPress={() => toggleColumn(key)}
                            >
                              <View
                                style={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: 4,
                                  borderWidth: 1.5,
                                  borderColor: isVisible ? "#56B7FD" : "#D1D5DB",
                                  backgroundColor: isVisible ? "#56B7FD" : "#fff",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  marginRight: 10,
                                }}
                              >
                                {isVisible && (
                                  <Feather name="check" size={13} color="#fff" />
                                )}
                              </View>
                              <Text
                                style={{
                                  fontSize: 14,
                                  color: isVisible ? "#111827" : "#9CA3AF",
                                  fontWeight: isVisible ? "600" : "400",
                                }}
                              >
                                {(value as any).name}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </ScrollView>
                    </Pressable>
                  </Pressable>
                </RNModal>
              )}
            </View>
          ) : (
            <View>
              <Spinner
                size="small"
                color="#74BBFF"
                styles={{ flexDirection: "row", margin: "20%" }}
              />
            </View>
          )}
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
    // marginTop: 20
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
  },
});
